import * as React from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import {
    PhoneNumber, Address, EmailAddress,
    RelatedParty, RelatedIndividual, RelatedOrganisation
} from '../../matterDetails/parties/PartiesInfo';
// import { RvContactCard } from './ContactCard';
import { RvContactTable } from './ContactTable';
// import { RvOrganisationCard } from './OrganisationCard';
import { RvOrganizationTable } from './OrganizationTable';
import { BorderedInfoTable, BorderedInfoTableField } from '../DataDisplay/BorderedInfoTable';

// Explicit request: give the address/phone/email groups shared across the Staff and Parties
// tabs an icon + label heading, matching the "panels, icons" modernisation asked for on the
// Internal Responsibilities (Staff) tab.
const usePartySectionStyles = makeStyles((theme: Theme) =>
    createStyles({
        section: {
            marginBottom: theme.spacing(1),
            '&:last-child': {
                marginBottom: 0,
            },
        },
        heading: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: theme.spacing(1),
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: theme.palette.text.secondary,
        },
        icon: {
            fontSize: 15,
            color: 'var(--rv-accent-indigo)',
        },
    })
);

const PartyInfoSection: React.FC<{ icon: React.ReactNode; label: string; fields: BorderedInfoTableField[] }> = ({ icon, label, fields }) => {
    const classes = usePartySectionStyles();

    if (fields.length === 0) {
        return null;
    }

    return (
        <div className={classes.section}>
            <div className={classes.heading}>
                <span className={classes.icon}>{icon}</span>
                {label}
            </div>
            <BorderedInfoTable fields={fields} />
        </div>
    );
};

// Explicit request: no table treatment on the Staff tab — a plain icon + value row per
// contact detail instead (email shown as a clickable mailto link), used only when
// renderCommon is called with layout="inline" (the Parties tab keeps the bordered table).
const useContactRowStyles = makeStyles((theme: Theme) =>
    createStyles({
        row: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: theme.spacing(1, 0),
            fontSize: 13.5,
            color: theme.palette.text.primary,
        },
        icon: {
            fontSize: 16,
            color: 'var(--rv-accent-indigo)',
            flexShrink: 0,
        },
        type: {
            fontSize: 12,
            color: theme.palette.text.secondary,
        },
    })
);

const PartyContactRow: React.FC<{ icon: React.ReactNode; type?: string; children: React.ReactNode }> = ({ icon, type, children }) => {
    const classes = useContactRowStyles();

    return (
        <div className={classes.row}>
            <span className={classes.icon}>{icon}</span>
            <span>{children}</span>
            {!!type && <span className={classes.type}>({type})</span>}
        </div>
    );
};

// Explicit request: no table treatment on the Parties tab either — a plain "Label: value"
// line for the handful of one-off fields (Reference, ABN, ACN, etc.) that aren't part of the
// address/phone/email groups above.
const usePlainFieldStyles = makeStyles((theme: Theme) =>
    createStyles({
        row: {
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
            padding: theme.spacing(0.5, 0),
            fontSize: 13.5,
        },
        label: {
            fontWeight: 600,
            color: theme.palette.text.secondary,
        },
        value: {
            color: theme.palette.text.primary,
        },
    })
);

export const PartyPlainField: React.FC<{ label: React.ReactNode; value: React.ReactNode }> = ({ label, value }) => {
    const classes = usePlainFieldStyles();

    return (
        <div className={classes.row}>
            <span className={classes.label}>{label}:</span>
            <span className={classes.value}>{value}</span>
        </div>
    );
};

export interface PartyCommonCardProps {
    phoneNumbers?: Array<PhoneNumber>;
    addresses?: Array<Address>;
    emailAddresses?: Array<EmailAddress>;
    relatedParties?: Array<RelatedParty>;
    relatedIndividuals?: Array<RelatedIndividual>;
    relatedOrganisations?: Array<RelatedOrganisation>;
}

export class PartyCommon {

    static comparePhoneNumbers(value1: PhoneNumber, value2: PhoneNumber): boolean {
        return (value1.type === value1.type && value1.number === value2.number);
    }

    static compareAddresses(value1: Address, value2: Address): boolean {
        return (value1.type === value1.type && value1.display === value2.display);
    }

    static compareEmailAddresses(value1: EmailAddress, value2: EmailAddress): boolean {
        return (value1.type === value1.type && value1.email === value2.email);
    }

    // tslint:disable-next-line:max-line-length
    static filterDuplicate<T>(values: Array<T>, parentValues: Array<T> | undefined, comparer: (value1: T, value2: T) => boolean): Array<T> {
        if (!parentValues) {
            return values;
        }
        var results = new Array<T>();
        for (var i = 0; i < values.length; i++) {
            var value = values[i];
            var exists = false;
            for (var j = 0; j < parentValues.length; j++) {
                if (comparer(value, parentValues[j])) {
                    exists = true;
                    break;
                }
            }
            if (!exists) {
                results.push(value);
            }
        }
        return results;
    }

    static renderCommon(props: PartyCommonCardProps, infoPanelItems: Array<{}>, layout: 'table' | 'inline' = 'table') {

        if (props.addresses && props.addresses.length > 0) {
            if (layout === 'inline') {
                props.addresses.forEach((address, idx) => {
                    infoPanelItems.push(
                        <PartyContactRow key={'address-' + idx} icon={<LocationOnOutlinedIcon fontSize="inherit" />} type={address.type}>
                            <a
                                href={'http://maps.google.com/maps?q=' + address.display}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {address.display ? address.display.replace(new RegExp('/r/n', 'g'), ' ') : ''}
                            </a>
                        </PartyContactRow>
                    );
                });
            } else {
                var addressFields: BorderedInfoTableField[] = props.addresses
                    // TODO: .sort((i1, i2): number => { return i2.sequence - i1.sequence; })
                    .map((address) => ({
                        label: address.type,
                        value: (
                            <a
                                href={'http://maps.google.com/maps?q=' + address.display}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {address.display ? address.display.replace(new RegExp('/r/n', 'g'), ' ') : ''}
                            </a>
                        ),
                    }));
                infoPanelItems.push(<PartyInfoSection key="addresses" icon={<LocationOnOutlinedIcon fontSize="inherit" />} label="Addresses" fields={addressFields} />);
            }
        }

        if (props.phoneNumbers && props.phoneNumbers.length > 0) {
            if (layout === 'inline') {
                props.phoneNumbers.forEach((phoneNumber, idx) => {
                    infoPanelItems.push(
                        <PartyContactRow key={'phone-' + idx} icon={<PhoneOutlinedIcon fontSize="inherit" />} type={phoneNumber.type}>
                            {phoneNumber.number}
                        </PartyContactRow>
                    );
                });
            } else {
                var phoneFields: BorderedInfoTableField[] = props.phoneNumbers
                    // TODO: .sort((i1, i2): number => { return i2.sequence - i1.sequence; })
                    .map((phoneNumber) => ({
                        label: phoneNumber.type,
                        value: phoneNumber.number,
                    }));
                infoPanelItems.push(<PartyInfoSection key="phones" icon={<PhoneOutlinedIcon fontSize="inherit" />} label="Phone Numbers" fields={phoneFields} />);
            }
        }

        if (props.emailAddresses && props.emailAddresses.length > 0) {
            if (layout === 'inline') {
                props.emailAddresses.forEach((emailAddress, idx) => {
                    infoPanelItems.push(
                        <PartyContactRow key={'email-' + idx} icon={<EmailOutlinedIcon fontSize="inherit" />} type={emailAddress.type}>
                            <a href={`mailto:${emailAddress.email}`}>{emailAddress.email}</a>
                        </PartyContactRow>
                    );
                });
            } else {
                var emailFields: BorderedInfoTableField[] = props.emailAddresses
                    // TODO: .sort((i1, i2): number => { return i2.sequence - i1.sequence; })
                    .map((emailAddress) => ({
                        label: emailAddress.type,
                        value: <a href={`mailto:${emailAddress.email}`}>{emailAddress.email}</a>,
                    }));
                infoPanelItems.push(<PartyInfoSection key="emails" icon={<EmailOutlinedIcon fontSize="inherit" />} label="Email Addresses" fields={emailFields} />);
            }
        }

        if (props.relatedParties) {
            var relatedOrganisations = props.relatedOrganisations;
            var relatedIndividuals = props.relatedIndividuals;

            var relatedParties = props.relatedParties
                .map((relatedParty, idx) => {
                    if (relatedOrganisations && relatedParty.partyEnumType === 'ORGANISATION') {
                        var relatedOrganisation = relatedOrganisations.find(o => o.partyId === relatedParty.partyId);
                        if (relatedOrganisation) {
                            // tslint:disable-next-line:max-line-length
                            var orgPhoneNumbers = this.filterDuplicate(relatedParty.phoneNumbers, props.phoneNumbers, this.comparePhoneNumbers);
                            // tslint:disable-next-line:max-line-length
                            var orgEmailAddresses = this.filterDuplicate(relatedParty.emailAddresses, props.emailAddresses, this.compareEmailAddresses);
                            // tslint:disable-next-line:max-line-length
                            var orgAdresses = this.filterDuplicate(relatedParty.addresses, props.addresses, this.compareAddresses);
                            return (
                                <RvOrganizationTable
                                    key={idx}
                                    data={relatedOrganisation}
                                    phoneNumbers={orgPhoneNumbers}
                                    emailAddresses={orgEmailAddresses}
                                    addresses={orgAdresses}
                                    tableClassName="depth-2"
                                />
                                // <RvOrganisationCard
                                //     key={idx}
                                //     iconKey="building"
                                //     data={relatedOrganisation}
                                //     phoneNumbers={orgPhoneNumbers}
                                //     emailAddresses={orgEmailAddresses}
                                //     addresses={orgAdresses}
                                    
                                // />
                            );
                        }
                    } else if (relatedIndividuals) {
                        var relatedIndividual = relatedIndividuals.find(o => o.id === relatedParty.id);
                        if (relatedIndividual) {
                            // tslint:disable-next-line:max-line-length
                            var indPhoneNumbers = this.filterDuplicate(relatedParty.phoneNumbers, props.phoneNumbers, this.comparePhoneNumbers);
                            // tslint:disable-next-line:max-line-length
                            var indEmailAddresses = this.filterDuplicate(relatedParty.emailAddresses, props.emailAddresses, this.compareEmailAddresses);
                            // tslint:disable-next-line:max-line-length
                            var indAdresses = this.filterDuplicate(relatedParty.addresses, props.addresses, this.compareAddresses);
                            return (
                                <RvContactTable
                                    key={idx}
                                    data={relatedIndividual}
                                    phoneNumbers={indPhoneNumbers}
                                    emailAddresses={indEmailAddresses}
                                    addresses={indAdresses}
                                    tableClassName="depth-2"
                                />
                            );
                        }
                    }
                    return null;
                });
            if (relatedParties.length > 0) {
                infoPanelItems.push(relatedParties);
            }
        }

    }
}
